import { List } from 'immutable';
// CORE
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { DateTime } from 'luxon';

import { accountsSelectors, accountsUtils } from 'app-components/flux/accounts';
import type { Account } from 'app-components/flux/accounts';
import { scheduledTransactionsSelectors } from 'app-components/flux/scheduled-transactions';
import { transactionsSelectors, transactionsUtils } from 'app-components/flux/transactions';
import type { transactionsTypes } from 'app-components/flux/transactions';
import { transactionListSelectors } from 'app-components/flux/transaction-list';

import { rootUiActions } from 'app-components/flux/root-ui';
// Data
import { useAppDispatch, useAppSelector } from 'hooks/redux';

import Box from '@mui/material/Box';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
// MUI
import { makeStyles } from 'tss-react/mui';

import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogTitle from 'components/Dialogs/StdDialogTitle';
import { useStandardDrawerVariant } from 'components/Drawers/StandardDrawer';
import ConnectedAvatar from 'components/ConnectedAvatar';
import { getSeriesIconKind } from 'images/icons/SeriesIcon';
import SearchBox from 'components/SearchBox';
import AccountMultiSelect from 'components/SimplifiSelects/AccountMultiSelect';
import TransactionSelectList from 'components/TransactionSelectList';
import AmountField, { ShowColorEnum, ShowSignEnum } from 'components/QuickenControls/AmountField';
// Custom
import useTxnActions from 'data/transactionActions/useTxnActions';
import type { RootState } from 'store';
import { DIALOG_TYPE } from './actions';
import type { MatchTxnDialogProps } from './actions';

const LIST_MIN_HEIGHT = 400;
const LIST_MAX_HEIGHT = 572;
const TRANSACTION_ITEM_SIZE = 60;

const useStyles = makeStyles()((theme) => ({
  dialogRoot: {
    padding: '0px !important',
    display: 'flex',
    flexDirection: 'column',
  },
  searchRow: {
    display: 'flex',
    width: '100%',
    padding: theme.spacing(1, 2),
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'nowrap',
  },
  accountSelect: {
    display: 'flex',
    flex: 1,
    minWidth: 0,
  },
  search: {
    display: 'flex',
    flex: '0 0 auto',
    justifyContent: 'flex-end',
  },
  searchBar: {
    width: 242,
    height: 36,
    margin: 0,
    paddingLeft: 4,
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.light}`,
    '&:hover': {
      bordercolor: theme.palette.text.primary,
    },
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
    },
  },
  searchInput: {
    fontSize: '14px',
    lineHeight: 1.43,
    fontWeight: 'normal',
    color: theme.palette.text.primary,
    '&:-webkit-autofill': {
      // disable autofill color because it makes it ugly
      WebkitBackgroundClip: 'text',
    },
  },
}));

const parseTransaction = (serializedTransaction: string): transactionsTypes.TransactionProps | null => {
  try {
    const transaction: unknown = JSON.parse(serializedTransaction);
    if (typeof transaction === 'object' && transaction !== null) {
      const id = 'id' in transaction ? transaction.id : undefined;
      const amount = 'amount' in transaction ? transaction.amount : undefined;
      const postedOn = 'postedOn' in transaction ? transaction.postedOn : undefined;
      if (typeof id === 'string' && typeof amount === 'number' && typeof postedOn === 'string') {
        return { id, amount, postedOn };
      }
    }
  } catch {
    // Ignore malformed row data rather than letting a click crash the dialog.
  }
  return null;
};

const ReminderToLinkSummary = ({ txn }: { txn: transactionsTypes.TransactionProps }) => {
  const theme = useTheme();
  const model = useAppSelector((state) =>
    scheduledTransactionsSelectors.getScheduledTransactionById(state, txn?.stModelId || ''),
  );
  const isIncome = useAppSelector((state) =>
    model?.transaction
      ? scheduledTransactionsSelectors.isIncomeSeriesTxn(state, model.transaction)
      : transactionsSelectors.isIncomeTxn(state, txn),
  );
  const avatarColor = isIncome ? theme.palette.legend.income : undefined;

  const dueDateIso = txn?.dueOn ?? txn?.postedOn;
  const dueDateLabel =
    dueDateIso && DateTime.fromISO(dueDateIso).isValid
      ? DateTime.fromISO(dueDateIso).toLocaleString(DateTime.DATE_MED)
      : '';

  return (
    <Stack direction='row' gap={1} sx={{ pt: 2, px: 2, pb: 0.5, alignItems: 'center' }}>
      <Typography variant='paragraphRegular' color='text.tertiary' noWrap>
        Reminder to link:
      </Typography>
      <ConnectedAvatar
        kind={getSeriesIconKind(transactionsUtils.isRefundTxn(txn), isIncome)}
        model={model}
        txn={txn}
        size={24}
        iconSize={10}
        color={avatarColor}
      />
      <Stack
        direction='row'
        alignItems='center'
        gap={1}
        sx={{ minWidth: 0, flex: 1, flexWrap: 'nowrap' }}
        divider={
          <Typography variant='paragraphRegular' color='text.disabled'>
            ·
          </Typography>
        }
      >
        <Typography variant='paragraphMedium' noWrap>
          {txn?.payee}
        </Typography>
        <AmountField
          value={txn?.amount}
          textProps={{ variant: 'captionRegular' }}
          showSign={ShowSignEnum.POSITIVE_ONLY}
          showColor={ShowColorEnum.POSITIVE_ONLY}
          variant='captionRegular'
        />
        <Typography variant='captionRegular' color='text.secondary' noWrap>
          {dueDateLabel}
        </Typography>
        <Typography variant='captionRegular' color='text.secondary' noWrap>
          {accountsUtils.getAccountString(txn?.accountId)}
        </Typography>
      </Stack>
    </Stack>
  );
};

const MatchTxnDialog = (props: MatchTxnDialogProps): React.JSX.Element | null => {
  const { classes } = useStyles();
  const { txn, transactions, rootModalDrawer, wrapperId = '', onLinked } = props;
  const dispatch = useAppDispatch();
  const drawerVariant = useStandardDrawerVariant();
  const isInStandardDrawer = drawerVariant !== null;
  const isOverlayDrawer = drawerVariant === 'overlay';
  const isDrawer = Boolean(rootModalDrawer || isInStandardDrawer);
  const showDrawerBackButton = Boolean(rootModalDrawer && isOverlayDrawer);

  const [open, setOpen] = useState(true);
  const [selectedTxn, setSelectedTxn] = useState<transactionsTypes.TransactionProps | null>(null);
  const [search, setSearch] = useState<string | null>(null);
  const [isLinking, setIsLinking] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState<(Account | undefined)[]>(() => [
    accountsSelectors.getAccountById(undefined, txn?.accountId),
  ]); // func so only evaluates once

  const { matchTransactions } = useTxnActions({ wrapperId });

  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  const onClose = useCallback(() => {
    setSearch(null);
    setSelectedTxn(null);
    dispatch(rootUiActions.removeDialog(DIALOG_TYPE, isDrawer ? { animate: true } : undefined));
    if (!isDrawer) {
      setOpen(false);
    }
  }, [dispatch, isDrawer]);

  const accountIdList = useMemo(() => selectedAccounts.map((acc) => acc?.id), [selectedAccounts]);
  const selectedAccountIds = useMemo(
    () => List(selectedAccounts.map((account) => account?.id).filter((id): id is string => Boolean(id))),
    [selectedAccounts],
  );

  const getMatchingTransactions = useCallback(
    (state: RootState) =>
      transactionListSelectors.getSortedTransactionsForAccountIds(state, {
        matchTxn: txn,
        accountIds: selectedAccountIds,
        filter: search,
      }),
    [search, selectedAccountIds, txn],
  );

  // data stuff
  const matchOverdueTransaction = async (): Promise<void> => {
    if (!selectedTxn || isLinking) {
      return;
    }

    setIsLinking(true);
    try {
      await matchTransactions([selectedTxn, txn]);
      if (!isMountedRef.current) {
        return;
      }
      onClose();
      onLinked?.();
    } catch {
      if (!isMountedRef.current) {
        return;
      }
      // Failure feedback is handled by the transaction action pipeline; keep the dialog open for retry.
      setIsLinking(false);
    }
  };

  const selectTxn = (e: React.SyntheticEvent<HTMLDivElement>) => {
    const clickedTxn = parseTransaction(e.currentTarget.id);
    if (!clickedTxn) {
      return;
    }
    if (selectedTxn && selectedTxn.id === clickedTxn.id) {
      setSelectedTxn(null);
    } else {
      setSelectedTxn(clickedTxn);
    }
  };

  const updateAccountSelection = (accounts: (Account | undefined)[]) => {
    setSelectedAccounts(accounts);
    setSelectedTxn(null);
  };

  const title = 'Link transaction';
  const content = (
    <>
      <DialogContent className={classes.dialogRoot} sx={isDrawer ? { flex: '1 1 auto', minHeight: 0 } : undefined}>
        <ReminderToLinkSummary txn={txn} />
        <div className={classes.searchRow}>
          <div className={classes.accountSelect}>
            <AccountMultiSelect
              id='match-account-select'
              selectedItems={accountIdList}
              onSelected={updateAccountSelection}
            />
          </div>
          <div className={classes.search}>
            <SearchBox
              initialValue={search}
              onSearch={(key: string | null) => setSearch(key && key.toLowerCase())}
              autoSearch
              autoFocus
              collapsable={false}
              placeholder='Search transactions'
              classes={{
                root: classes.searchBar,
                input: classes.searchInput,
              }}
            />
          </div>
        </div>

        <Box sx={{ px: 2, py: 0 }}>
          <TransactionSelectList
            clickFunc={selectTxn}
            highlight={selectedTxn?.id}
            filter={search}
            onReset={() => setSearch(null)}
            transactions={transactions}
            transactionListSelector={getMatchingTransactions}
            listMinHeight={LIST_MIN_HEIGHT}
            listMaxHeight={LIST_MAX_HEIGHT}
            listItemSize={TRANSACTION_ITEM_SIZE}
          />
        </Box>
      </DialogContent>

      <StdDialogActions
        divider={isDrawer}
        primary={{
          children: 'Link',
          variant: 'contained',
          size: 'medium',
          onClick: matchOverdueTransaction,
          disabled: !selectedTxn || isLinking,
        }}
      />
    </>
  );

  if (isDrawer) {
    if (!open) {
      return null;
    }

    return (
      <Box
        aria-label={title}
        role='dialog'
        sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
      >
        <StdDialogTitle
          variant='drawer'
          id='dialog-title'
          onBack={showDrawerBackButton ? onClose : undefined}
          onClose={onClose}
          showBackButton={showDrawerBackButton}
          showCloseButton
        >
          {title}
        </StdDialogTitle>
        {content}
      </Box>
    );
  }

  return (
    <StdDialog onClose={onClose} open={Boolean(open)} sharedcomponentid='MATCH_TXN_DIALOG' size='md' title={title}>
      {content}
    </StdDialog>
  );
};

export default MatchTxnDialog;
